import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useForm } from "react-hook-form"
import Stack from "@mui/material/Stack"
import { CONFIRM_NEW_PASSWORD, MINIMUM_PASSWORD_LENGTH, NEW_PASSWORD, OTP_LENGTH } from "constants/index"
import { MIN_LENGTH_ZOD_ERROR } from "constants/errors"
import { zodResolver } from "@hookform/resolvers/zod"
import { resetPasswordSchema } from "validations/auth"
import { updateGlobalToastMessage } from "appRedux/slices/globalSlice"
import { useForgetPasswordMutation, useResetPasswordMutation } from "appRedux/slices/authApiSlice"
import VisibleIcon from "components/atoms/_icons/VisibleIcon"
import InvisibleIcon from "components/atoms/_icons/InvisibleIcon"
import CTA from "components/atoms/CTA"
import TextField from "components/atoms/TextField"
import BottomSheet from "components/atoms/BottomSheet"
import CountdownTimer from "components/atoms/CountdownTimer"
import { styled } from "@mui/material/styles"
import { OPEN_SEND_OTP_BOTTOM_SHEET, SHOW_NO_EMAIL_FOUND_MODAL } from "constants/events"
import { useAppSelector } from "appRedux/hooks"
import Typography from "@mui/material/Typography"

type SendOTPBottomSheetProps = {
  show: boolean;
  showEmail: boolean;
  email?: string;
  onOpen: () => void;
  onClose: () => void;
}

const formInitialValue = {
  otp: "",
  [NEW_PASSWORD]: "",
  [CONFIRM_NEW_PASSWORD]: ""
}

export default function ForgetPasswordBottomSheet(props: SendOTPBottomSheetProps) {
  const { show, showEmail, email, onOpen, onClose } = props

  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false)
  const [showResendEmailButton, setShowResendEmailButton] = useState<boolean>(false)

  const resendOTPRemainedTime = useAppSelector((state) => state.resetPassword.resendOTPRemainedTime)
  const forgetPasswordToken = useAppSelector((state) => state.resetPassword.forgetPasswordToken)

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [triggerResetPassword, resetPasswordResult] = useResetPasswordMutation()
  const [triggerForgetPassword, forgetPasswordResult] = useForgetPasswordMutation()

  const {
    register,
    getValues,
    setValue,
    reset,
    handleSubmit,
    formState: { errors, dirtyFields }
  } = useForm<ResetPasswordFormFields>({
    resolver: zodResolver(resetPasswordSchema),
    shouldFocusError: false,
    reValidateMode: "onBlur",
    defaultValues: formInitialValue
  })

  useEffect(() => {
    if (!show) return
    resetBottomSheetStates()
  }, [show])

  useEffect(() => {
    setShowResendEmailButton(false)
  }, [resendOTPRemainedTime])

  useEffect(() => {
    if (!Object.keys(errors).length) return
    console.log(errors)
    handleErrors()
  }, [errors])

  function resetBottomSheetStates() {
    reset(formInitialValue)
    setShowPassword(false)
    setShowConfirmPassword(false)
    setShowResendEmailButton(false)
  }

  function handleErrors() {
    if (errors.otp) {
      if (errors.otp.message?.includes("contain exactly")) {
        dispatch(updateGlobalToastMessage({
          severity: "error",
          key: "otp",
          show: true,
          status: 400,
          message: t("auth.otpLengthError", { length: OTP_LENGTH })
        }))
      } else {
        dispatch(updateGlobalToastMessage({
          severity: "error",
          key: "otp",
          show: true,
          status: 400,
          message: t("auth.invalidOTP")
        }))
      }
    } else if (errors[NEW_PASSWORD]) {
      dispatch(updateGlobalToastMessage({
        severity: "error",
        key: `password_${MIN_LENGTH_ZOD_ERROR}`,
        show: true,
        status: 400,
        message: t("errorMessage.minLengthError", { field: t("auth.password"), minLength: MINIMUM_PASSWORD_LENGTH })
      }))
    } else if (errors[CONFIRM_NEW_PASSWORD]) {
      dispatch(updateGlobalToastMessage({
        severity: "error",
        key: "confirmNewPassword",
        show: true,
        status: 400,
        message: "errorMessage.passwordsDontMatchException"
      }))
    }
  }

  function handleResetPassword(values: ResetPasswordFormFields) {
    triggerResetPassword({
      otp: values.otp,
      newPassword: values[NEW_PASSWORD],
      confirmNewPassword: values[CONFIRM_NEW_PASSWORD],
      token: forgetPasswordToken || ""
    })
  }

  function togglePasswordVisibility(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault()
    event.stopPropagation()
    setShowPassword(!showPassword)
  }

  function toggleConfirmPasswordVisibility(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault()
    event.stopPropagation()
    setShowConfirmPassword(!showConfirmPassword)
  }

  function handleResendEmail() {
    if (!email) {
      document.dispatchEvent(new CustomEvent(SHOW_NO_EMAIL_FOUND_MODAL))
      return
    }
    triggerForgetPassword(email)
  }

  function handleEditEmail() {
    document.dispatchEvent(new CustomEvent(OPEN_SEND_OTP_BOTTOM_SHEET))
  }

  function handleCountdownEnd() {
    setShowResendEmailButton(true)
  }

  return (
    <BottomSheet
      id="forget-password-bottom-sheet"
      title={t("auth.forgotPassword")}
      show={show}
      openBottomSheet={onOpen}
      closeBottomSheet={onClose}
    >
      <form onSubmit={handleSubmit(handleResetPassword)}
            style={{ width: "100%", marginTop: (showEmail && email) ? "10px" : "40px" }}>
        <Stack alignItems="center">
          {(showEmail && email) ? <EmailSection>
            <Email>{email}</Email>
            <CTA
              variant={"text"}
              onClick={handleEditEmail}
            >
              {t("auth.editEmail")}
            </CTA>
          </EmailSection> : null}

          <TextField
            id="otp"
            label={t("auth.otp")}
            required={true}
            type={"number"}
            defaultValue={getValues("otp")}
            register={register}
            setValue={setValue}
            error={errors.otp}
            alignment={"center"}
          />
          <TextField
            id={NEW_PASSWORD}
            label={t("auth.newPassword")}
            type={showPassword ? "text" : "password"}
            required={true}
            defaultValue={getValues(NEW_PASSWORD)}
            startAdornmentIcon={showPassword ? <VisibleIcon/> : <InvisibleIcon/>}
            onIconClick={togglePasswordVisibility}
            register={register}
            setValue={setValue}
            error={errors.otp ? undefined : errors[NEW_PASSWORD]}
            direction={"rtl"}
          />
          <TextField
            id={CONFIRM_NEW_PASSWORD}
            label={t("auth.confirmNewPassword")}
            type={showConfirmPassword ? "text" : "password"}
            required={true}
            defaultValue={getValues(CONFIRM_NEW_PASSWORD)}
            startAdornmentIcon={showConfirmPassword ? <VisibleIcon/> : <InvisibleIcon/>}
            onIconClick={toggleConfirmPasswordVisibility}
            register={register}
            setValue={setValue}
            error={errors[NEW_PASSWORD] ? undefined : errors[CONFIRM_NEW_PASSWORD]}
            direction={"rtl"}
          />

          <ResendEmailSection>
            {showResendEmailButton ? <CTA
              variant="text"
              color="gray"
              disabled={forgetPasswordResult.isLoading}
              loading={forgetPasswordResult.isLoading}
              onClick={handleResendEmail}
            >
              {t("general.resendRequest")}
            </CTA> : resendOTPRemainedTime && <ResendEmailTimerContainer>
              {t("general.resendRequest")}
                <CountdownTimer
                    targetTimestamp={resendOTPRemainedTime}
                    onCountdownEnd={handleCountdownEnd}
                />
            </ResendEmailTimerContainer>}
          </ResendEmailSection>

          <CTA
            variant="contained"
            type="submit"
            disabled={Object.keys(dirtyFields).length !== 3 || resetPasswordResult.isLoading}
            loading={resetPasswordResult.isLoading}
            onClick={handleSubmit(handleResetPassword)}
          >
            {t("general.submit")}
          </CTA>
        </Stack>
      </form>
    </BottomSheet>
  )
}

const EmailSection = styled(Stack)(({ theme }) => ({
  width: "100%",
  flexDirection: "row-reverse",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  marginBottom: "3.3rem",

  "& button": {
    width: "fit-content",
    whiteSpace: "nowrap",
    marginLeft: 0,
    marginRight: "auto"
  }
}))

const Email = styled(Typography)(({ theme }) => ({
  fontSize: "1.3rem",
  color: theme.palette.gray["13"],
  textAlign: "left"
}))

const ResendEmailSection = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  justifyContent: "center",
  marginTop: "-2.2rem",
  marginBottom: "1rem",

  "& button": {
    color: `${theme.palette.gray["16"]} !important`
  },

  "& button:disabled": {
    color: `${theme.palette.gray["12"]} !important`
  }
}))

const ResendEmailTimerContainer = styled(Stack)(({ theme }) => ({
  height: "48px",
  flexDirection: "row",
  justifyContent: "center",
  gap: "4px",
  fontSize: "1.3rem",
  color: theme.palette.gray["12"],
  padding: "10px 6px"
}))
